import { useState } from 'react';
import { Play, Pause } from 'lucide-react';
import styles from './progress.module.css';

interface VideoProgressProps {
  initialProgress?: number;
  className?: string;
  duration?: number;
  onChange?: (progress: number) => void;
  onPlayToggle?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

const VideoProgress = ({
  initialProgress = 0,
  className,
  duration = 100,
  onChange,
  onPlayToggle,
  onDragStart,
  onDragEnd,
}: VideoProgressProps) => {
  const [seek, setSeek] = useState({ source: initialProgress, value: initialProgress });
  const progress = Object.is(seek.source, initialProgress) ? seek.value : initialProgress;
  if (!Object.is(seek.source, initialProgress)) {
    setSeek({ source: initialProgress, value: initialProgress });
  }
  const [isPlaying, setIsPlaying] = useState(true);
  const fillPercent = duration > 0 && Number.isFinite(progress / duration)
    ? Math.max(0, Math.min(100, (progress / duration) * 100))
    : 0;

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newProgress = parseFloat(e.target.value);
    setSeek({ source: initialProgress, value: newProgress });
    if (onChange) onChange(newProgress);
  };

  // Süreyi formatla (saniye -> MM:SS)
  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds === 0) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayToggle = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    if (onPlayToggle) onPlayToggle();
  };

  const handleDragStart = () => {
    if (isPlaying) {
      setIsPlaying(false);
      if (onPlayToggle) onPlayToggle();
    }
    if (onDragStart) onDragStart();
  };

  const handleDragEnd = () => {
    setIsPlaying(true);
    if (onDragEnd) onDragEnd();
  };

  return (
    <div className={`absolute flex flex-row items-center ${className}`}>
      <button
        className="p-2 rounded-full text-white hover:text-white/70 outline-none border-none mr-2"
        onClick={handlePlayToggle}
      >
        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
      </button>
      <div className={styles.progressContainer}>
        <input
          style={{ background: `linear-gradient(90deg, #ffffff ${fillPercent}%, #868686 ${fillPercent}%)` }}
          type="range"
          min="0"
          max={duration}
          value={progress}
          onChange={handleProgressChange}
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
          className={styles.inputProgressRange}
        />
      </div>
      <div className="ml-2 text-white text-[14px]">{formatTime(progress)}</div>
    </div>
  );
};

export default VideoProgress;
